import { useHomepage } from "@reco/shared/src/composables/useHomepage";
import { GridBoxType, Scenario } from "@reco/shared/src/constants";
import type {
  RecommendationsConfig,
  RecommendationsResult,
  ShopTheLookResult,
  StandardRecommendationsResult,
} from "@reco/shared/src/types/recommendation";
import { Feature, shouldUseFeature } from "../FeatureFlags";
import { SourceType } from "../types/records";
import { getContextId } from "./params";
import { getVisitHistory } from "./storage";

export const MINDSHIFT_OS_COUNTRIES = ["DE", "BE", "SK", "CZ", "PL", "FR", "NL", "ES"];

/**
 ** OneApp: Android: salesChannel=06, iOS: salesChannel=07
 ** EcomApp : Android: salesChannel=05, iOS: salesChannel=02
 ** Web: salesChannel=01
 */
export function isMobileEmbeddedView(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const salesChannel = urlParams.get("salesChannel") || "";

  return ["02", "05", "06", "07", "2", "5", "6", "7"].includes(salesChannel);
}

export function getGridboxType(countryCode?: string, recommendationList?: string): GridBoxType {
  if (!countryCode) return GridBoxType.DEFAULT;

  const { isHomepage } = useHomepage();
  const isMindshiftCountry = MINDSHIFT_OS_COUNTRIES.includes(countryCode.toUpperCase());

  if (recommendationList?.includes("cartoverlay")) return GridBoxType.HORIZONTAL_REDUCED;

  if (isHomepage.value && isMindshiftCountry) {
    if (recommendationList?.includes("last_seen")) {
      return GridBoxType.MINIMAL;
    } else if (!shouldUseFeature(Feature.SERECA, countryCode)) {
      return GridBoxType.REDUCED;
    }
  }

  return shouldUseFeature(Feature.SERECA, countryCode) ? GridBoxType.SERECA : GridBoxType.DEFAULT;
}

/**
 * Gets the product ID from the URL
 * @param pageType The type of the page, e.g., "pdp"
 * @param pathname The pathname of the URL
 * @returns The product ID extracted from the URL
 * @throws Error if the product ID cannot be extracted
 */
export const getProductIDFromURL = (pageType: string, pathname: string): string => {
  const productId = getContextId(pageType, pathname);
  if (!productId) {
    throw new Error("Unable to extract product ID from URL");
  }
  return productId;
};

/**
 * Gets product IDs from the visit history
 * @returns Comma separated list of product IDs from visit history
 * @throws Error if no valid product IDs are found in visit history
 */
export const getProductIDsFromVisitHistory = (): string => {
  const visitHistory = getVisitHistory();
  if (!visitHistory) {
    throw new Error("Visit history is required for source 'history' but was not provided.");
  }
  const productIds = visitHistory.map((record) => record.params.contextProduct).join(",");

  if (productIds.length === 0) {
    throw new Error("No valid product IDs found in visit history");
  }

  return productIds;
};

/**
 * Gets product IDs based on the source type (currently supports "url" and "history")
 * @param params Parameters for getting product IDs
 * @returns String single product ID or comma separated list of product IDs, depending on source type
 */
export const getProductIDsBasedOnSource = (source: SourceType, params: RecommendationsConfig): string => {
  if (source === SourceType.URL) {
    return getProductIDFromURL(params.pageType, params.pathname);
  } else if (source === SourceType.HISTORY) {
    return getProductIDsFromVisitHistory();
  } else {
    throw new Error(`Unsupported source type: ${source}`);
  }
};

export const isShopTheLookScenario = (scenario: Scenario): scenario is Scenario.SHOPTHELOOK => {
  return scenario === Scenario.SHOPTHELOOK;
};

export const isStandardRecommendationsResult = (
  result: RecommendationsResult,
): result is StandardRecommendationsResult => "recommendations" in result;

export const isShopTheLookResult = (result: RecommendationsResult): result is ShopTheLookResult =>
  "shopTheLookItems" in result;
